import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { GraduationCap, Send, Loader2, ChevronDown, ChevronRight } from "lucide-react";

const scoreColor = (s) => (s >= 75 ? "#10B981" : s >= 50 ? "#F59E0B" : "#EF4444");
const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s || "—"; } };

export default function TrainerConsole() {
  const [trainees, setTrainees] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [results, setResults] = useState([]);
  const [trainee, setTrainee] = useState("");
  const [scenario, setScenario] = useState("");
  const [busy, setBusy] = useState(false);
  const [expand, setExpand] = useState(null);

  const load = async () => {
    const [t, s, r] = await Promise.all([
      client.get("/training/trainees"),
      client.get("/training/scenarios"),
      client.get("/training/results"),
    ]);
    setTrainees(t.data); setScenarios(s.data); setResults(r.data);
    if (!trainee && t.data[0]) setTrainee(t.data[0].id);
    if (!scenario && s.data[0]) setScenario(s.data[0].key);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const assign = async () => {
    if (!trainee || !scenario) { toast.error("Pick a trainee and a scenario"); return; }
    setBusy(true);
    try {
      await client.post("/training/assign", { trainee_id: trainee, scenario_key: scenario });
      const tn = trainees.find((x) => x.id === trainee)?.name;
      toast.success(`Scenario assigned to ${tn}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
    finally { setBusy(false); }
  };

  const inp = "w-full bg-[#141518] border border-[#2C2D35] px-3 py-2 text-sm outline-none focus:border-[#8B5CF6] font-mono text-white";

  return (
    <Layout>
      <PageHeader title="Trainer Console" sub="Assign practice scenarios to trainees and review scored after-actions" />
      <div className="flex-1 overflow-y-auto p-6 grid-lines space-y-5">
        {/* assign */}
        <div className="border border-[#8B5CF6]/40 bg-[#8B5CF6]/[0.06]">
          <div className="px-3 py-2 border-b border-[#8B5CF6]/30 font-head text-xs font-semibold text-[#8B5CF6] flex items-center gap-2">
            <GraduationCap size={14} /> ASSIGN A TRAINING SCENARIO
          </div>
          <div className="p-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <div>
              <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Trainee</label>
              <select data-testid="trainer-select-trainee" value={trainee} onChange={(e) => setTrainee(e.target.value)} className={inp}>
                {trainees.length === 0 && <option value="">No training accounts yet</option>}
                {trainees.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.role?.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] text-[#9CA3AF] uppercase">Scenario</label>
              <select data-testid="trainer-select-scenario" value={scenario} onChange={(e) => setScenario(e.target.value)} className={inp}>
                {scenarios.map((s) => <option key={s.key} value={s.key}>{s.title} ({s.disc.toUpperCase()} · P{s.priority})</option>)}
              </select>
            </div>
            <button data-testid="trainer-assign-btn" onClick={assign} disabled={busy || !trainees.length}
              className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-head bg-[#8B5CF6] text-white hover:bg-[#7c3aed] disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Assign
            </button>
          </div>
          {trainees.length === 0 && (
            <div className="px-3 pb-3 font-mono text-[11px] text-[#F59E0B]">Provision training accounts in Administration (Environment = Training) first.</div>
          )}
        </div>

        {/* results */}
        <div className="border border-[#2C2D35]">
          <div className="px-3 py-2 border-b border-[#2C2D35] font-head text-xs font-semibold text-[#9CA3AF]">
            COMPLETED AFTER-ACTIONS ({results.length})
          </div>
          {results.length === 0 ? (
            <div className="p-6 text-center font-mono text-xs text-[#6B7280]" data-testid="trainer-no-results">No completed training sessions yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] text-[#6B7280] uppercase border-b border-[#2C2D35]">
                  <th className="px-3 py-2"></th><th className="px-3 py-2">Trainee</th><th className="px-3 py-2">Scenario</th>
                  <th className="px-3 py-2">Score</th><th className="px-3 py-2">Case#</th><th className="px-3 py-2">Completed</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <React.Fragment key={r.id}>
                    <tr data-testid={`trainer-result-${r.id}`} className="border-b border-[#2C2D35] hover:bg-[#141518] cursor-pointer" onClick={() => setExpand(expand === r.id ? null : r.id)}>
                      <td className="px-3 py-2">{expand === r.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                      <td className="px-3 py-2 font-head">{r.trainee_name}</td>
                      <td className="px-3 py-2">{r.scenario_title}</td>
                      <td className="px-3 py-2 font-head font-bold" style={{ color: scoreColor(r.score) }}>{r.score}%</td>
                      <td className="px-3 py-2 font-mono text-xs text-[#9CA3AF]">{r.cc_number || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-[#9CA3AF]">{fmt(r.completed_at)}</td>
                    </tr>
                    {expand === r.id && (
                      <tr className="bg-[#0d0e11]"><td></td><td colSpan={5} className="px-3 py-2">
                        <div className="space-y-1">
                          {(r.breakdown || []).map((b, i) => (
                            <div key={i} className="font-mono text-[11px]">
                              <span style={{ color: b.pass ? "#10B981" : "#EF4444" }}>{b.pass ? "✓" : "✗"}</span>
                              {" "}<span className="text-[#F3F4F6]">{b.label}:</span> <span className="text-[#9CA3AF]">{b.got}</span>
                              {!b.pass && <span className="text-[#F59E0B]"> · ideal: {b.expected}</span>}
                            </div>
                          ))}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
